import withQuery from 'with-query';

import {
  FavStock,
  FavStockRequest,
  ListFavEventsRequest,
  ListFavEventsResponse,
  ListFavStockDetailsResponse,
  ListFavStockResponse,
  RemoveFavStockRequest,
} from '../py-generated/datalouder/favorite_stock/types';
import {
  Feature,
  ListFeatureFlagsResponse,
} from '../py-generated/datalouder/feature_flag/types';
import {
  Category,
  StockSearchResponse,
} from '../py-generated/datalouder/stock/types';
import {
  Course,
  NOPStrategyResultResponse,
  Post,
  Region,
  Tutorial,
} from '../shared/types';
import { apiUrl, apiv2Url } from './config';
import { getOption, getPromise, postPromise, RequestOptions } from './utils';

const listTutorialVideos = (abortSignal: AbortSignal): Promise<any> =>
  getPromise(`${apiv2Url}/video-tutorials`, {}, data => data, abortSignal);

const listYoutubePlaylistVideo = (
  abortSignal: AbortSignal,
  playListId: string
): Promise<any> =>
  getPromise(
    `${apiv2Url}/list-video-tutorial`,
    { playListId },
    data => data,
    abortSignal
  );

const addTutorialVideo = (
  abortSignal: AbortSignal,
  title: string,
  url: string,
  order: number
): Promise<any> =>
  postPromise(
    `${apiv2Url}/create-video-tutorial`,
    { title, url, order },
    data => data,
    abortSignal
  );

const updateTutorialVideo = (
  abortSignal: AbortSignal,
  id: number,
  title: string,
  url: string
): Promise<any> =>
  postPromise(
    `${apiv2Url}/update-video-tutorial`,
    { id, title, url },
    data => data,
    abortSignal
  );

const orderTutorialVideos = (
  abortSignal: AbortSignal,
  order: number
): Promise<any> =>
  postPromise(
    `${apiv2Url}/order-video-tutorial`,
    { order },
    data => data,
    abortSignal
  );

const searchStockCode = (
  abortSignal: AbortSignal,
  stockCode: string
): Promise<StockSearchResponse> =>
  getPromise(
    `${apiv2Url}/searchStockCode`,
    {
      keyword: stockCode,
    },
    undefined,
    abortSignal
  );

const listFavoriteStocks = (): Promise<ListFavStockResponse> =>
  getPromise(`${apiv2Url}/favorite-stock`, {}, data => data);

const listFavoriteStockDetails = (): Promise<ListFavStockDetailsResponse> =>
  getPromise(`${apiv2Url}/favorite-stock-details`, {}, data => data);

const listFavoriteStockEvents = (
  category: Category,
  stockId: string
): Promise<ListFavEventsResponse> =>
  getPromise<ListFavEventsRequest, ListFavEventsResponse>(
    `${apiv2Url}/favorite-stock-events`,
    { category, stock_id: stockId },
    data => data
  );

const addFavoriteStock = (
  category: Category,
  stockId: string,
  unlike: boolean
): Promise<FavStock> =>
  postPromise<FavStockRequest, FavStock>(`${apiv2Url}/favorite-stock`, {
    category,
    stock_id: stockId,
    unlike,
  });

const addFavoriteStockLegacy = (
  stockCode: string,
  unlike: boolean,
  region: Region
): Promise<void> =>
  postPromise(`${apiUrl}/misc/addFavoriteStock`, { region, stockCode, unlike });

const removeFavoriteStock = (
  category: Category,
  stockId: string
): Promise<void> =>
  postPromise<RemoveFavStockRequest, void>(
    `${apiv2Url}/favorite-stock/remove`,
    { category, stock_id: stockId }
  );

const removeFavoriteStockLegacy = (
  stockCode: string,
  region: Region
): Promise<void> =>
  postPromise(`${apiUrl}/misc/removeFavoriteStock`, { region, stockCode });

const loadUserWinRates = (
  abortSignal: AbortSignal,
  region: Region
): Promise<any> =>
  getPromise(
    `${apiUrl}/misc/loadUserWinRates`,
    { region },
    data => data,
    abortSignal
  );

const listPosts = (abortSignal: AbortSignal): Promise<Post[]> =>
  getPromise(`${apiv2Url}/posts`, {}, undefined, abortSignal);

const getPostReads = (abortSignal: AbortSignal): Promise<any> =>
  getPromise(`${apiv2Url}/notification/read`, {}, undefined, abortSignal);

const readPost = (abortSignal: AbortSignal, postId: number): Promise<any> =>
  postPromise(
    `${apiv2Url}/notification/read`,
    { postId },
    undefined,
    abortSignal
  );

const storePreference = <T>(
  abortSignal: AbortSignal,
  name: string,
  value: T
): Promise<void> =>
  postPromise(
    `${apiv2Url}/preference`,
    {
      name,
      value,
    },
    undefined,
    abortSignal
  );

const loadPreference = (
  abortSignal: AbortSignal
): Promise<Record<string, any>> =>
  getPromise(`${apiv2Url}/preference`, {}, undefined, abortSignal);

const getStrapiTreeContent = (
  abortSignal: AbortSignal,
  nodeType: 'TUTORIAL' | 'FAQ',
  nodeId?: number
): Promise<Tutorial> => {
  const params = {
    type: nodeType,
  };
  if (nodeId) {
    params['id'] = nodeId;
  }
  return getPromise(`${apiv2Url}/cms`, params, undefined, abortSignal);
};

const getUserListForStockOptions = (stockCode: string): RequestOptions => {
  return {
    url: withQuery(`${apiUrl}/misc/userListForStock`, { stockCode }),
    ...getOption(),
  };
};

const listStocks = (abortSignal: AbortSignal): Promise<any> =>
  getPromise<any, any>(`${apiUrl}/misc/stocks`, {}, undefined, abortSignal);

const searchOptions = (query: string): RequestOptions => {
  return {
    url: withQuery(`${apiv2Url}/search`, { query }),
    ...getOption(),
  };
};

const getNopStrategyResult = (
  ticker: string
): Promise<NOPStrategyResultResponse> =>
  getPromise<{ ticker: string }, NOPStrategyResultResponse>(
    `${apiv2Url}/nopStrategyResult`,
    { ticker }
  );

const getFeatureFlags = (userId?: number): Promise<Feature[]> =>
  getPromise(`${apiv2Url}/feature-flags`, { userId });

const listFeatureFlags = (): Promise<ListFeatureFlagsResponse> =>
  getPromise(`${apiv2Url}/feature-flags/admin`, {});

const enableFeatureFlag = (feature: string, userId: number): Promise<void> =>
  postPromise(`${apiv2Url}/feature-flags/admin`, {
    action: 'Add',
    feature,
    user_id: userId,
  });

const disableFeatureFlag = (feature: string, userId: number): Promise<void> =>
  postPromise(`${apiv2Url}/feature-flags/admin`, {
    action: 'Remove',
    feature,
    user_id: userId,
  });

const confirmCourseSignup = (
  name: string,
  email: string,
  phone: string,
  timeslot: string,
  method: string,
  source: string
): Promise<void> =>
  postPromise(`${apiv2Url}/course/confirm`, {
    name,
    email,
    phone,
    timeslot,
    method,
    source,
  });

const listCourses = (disableCache?: boolean): Promise<Course[]> =>
  getPromise(`${apiv2Url}/courses`, {
    disableCache,
  });

const updateCourses = (courses: Course[]): Promise<void> =>
  postPromise(`${apiv2Url}/courses`, {
    courses,
  });

export default {
  listTutorialVideos,
  listYoutubePlaylistVideo,
  addTutorialVideo,
  updateTutorialVideo,
  orderTutorialVideos,
  searchStockCode,
  listFavoriteStocks,
  listFavoriteStockDetails,
  listFavoriteStockEvents,
  addFavoriteStock,
  addFavoriteStockLegacy,
  removeFavoriteStock,
  removeFavoriteStockLegacy,
  listPosts,
  getPostReads,
  readPost,
  storePreference,
  loadPreference,
  getStrapiTreeContent,
  loadUserWinRates,
  getUserListForStockOptions,
  listStocks,
  searchOptions,
  getNopStrategyResult,
  getFeatureFlags,
  listFeatureFlags,
  enableFeatureFlag,
  disableFeatureFlag,
  confirmCourseSignup,
  listCourses,
  updateCourses,
};
